import React, { useEffect, useMemo, useState } from 'react';
import { TypedDocumentNode, useMutation, useQuery } from '@apollo/client';
import type { match } from 'react-router';
import type { History } from 'history';
import pluralize from 'pluralize';
import { TitleService } from '../../services';
import { Breadcrumbs } from '../../store';
import routePaths from '../../route-paths';
import {
  AlertService,
  Button,
  DatePicker,
  Loading,
  Modal,
  ModalContent,
  ModalFooter,
  SelectOption,
  Theme,
} from '@spoiler-alert/ui-library';
import { createUseStyles } from 'react-jss';
import BillingProfileView from './billing-profile';
import {
  BillingContractType,
  BillingEventType,
  BillingProfile,
  ProfileSite,
  SelectItem,
  TabsCustomer,
} from './billing-manager-types';
import {
  BillingContractsListQuery,
  BillingCustomersListQuery,
  BillingEventTypeListQuery,
  BillingProfileBySiteIdQuery,
  BillingProfileCoverageQuery,
  SaveBillingProfile,
  TabsConnectionAuditQuery,
  UpdateBillingSchedule,
} from './billing-manager-queries';
import moment from 'moment';
import accounting from 'accounting';

const dateFormat = 'MMM Do, YYYY';

const useStyles = createUseStyles(
  {
    wrap: {
      display: 'flex',
      flexDirection: 'row',
      gap: 20,
      height: 'calc(100vh - 54px)',
    },
    headerRow: {
      display: 'flex',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 15,
    },
    select: {
      flexGrow: 1,
      maxWidth: 400,
    },
    errors: {
      display: 'flex',
      gap: 10,
      marginBottom: 10,
    },
    error: {
      border: `1px solid ${Theme.red}`,
      background: Theme.red10,
      color: Theme.redDark,
      padding: [4, 10],
    },
    view: {
      flexGrow: 1,
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      flexDirection: 'row',
      gap: 20,
    },
    noProfile: {
      extend: 'editBox',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    editBox: {
      boxShadow: '0px 0px 6px 0px rgba(0, 0, 0, 0.15)',
      padding: 20,
      flex: '1 1 50%',
      maxWidth: 500,
    },
    contractBox: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
    },
    singleContract: {
      display: 'flex',
      flexDirection: 'column',
      gap: 15,
      border: `1px solid ${Theme.borderColor}`,
      borderRadius: 10,
      padding: 15,
      '& h4': {
        fontSize: 18,
        margin: 0,
      },
      '& h5': {
        fontSize: 16,
        margin: 0,
      },
    },
    revenueTable: {
      width: '100%',
      textAlign: 'left',
      fontSize: 14,
      borderCollapse: 'collapse',
      '& td, & th': {
        padding: [5, 10],
      },
      '& tbody tr:nth-child(odd)>td': {
        background: Theme.grey10,
      },
    },
    posShift: {
      color: Theme.blue,
    },
    negShift: {
      color: Theme.orange,
    },
  },
  { name: 'BillingManagerEditor' },
);

interface EditingBillingContractType extends BillingContractType {
  newServiceStartDate: moment.Moment;
  touched: boolean;
}

interface BillingManagerEditorProps {
  history: History;
  match: match<{ orgSiteId: string }> | null;
}

const BillingManagerEditor: React.FC<BillingManagerEditorProps> = ({ history, match }: BillingManagerEditorProps) => {
  const classes = useStyles();
  const orgSiteId = match?.params.orgSiteId || '';

  if (orgSiteId === '') history.push(routePaths.billingManager);

  const [editingProfile, setEditingProfile] = useState<BillingProfile | undefined>();
  const [touched, setTouched] = useState<boolean>(false);
  const [editingContract, setEditingContract] = useState<EditingBillingContractType | null>();

  const { data: sitesData, loading: loadingSites } = useQuery(BillingProfileCoverageQuery);

  const { data: tabsCustomerData } = useQuery(BillingCustomersListQuery);

  const { data: tabsEventTypesData } = useQuery(BillingEventTypeListQuery);

  const { data: billingProfileData, loading: loadingProfile } = useQuery(BillingProfileBySiteIdQuery, {
    variables: {
      sellerSiteId: orgSiteId,
    },
    skip: !orgSiteId,
  });

  const refetchQueries = ['adminBillingProfileCoverageQuery', { query: TabsConnectionAuditQuery }];
  if (editingProfile?.id === '') refetchQueries.push('adminBillingProfileQuery');
  const [saveProfile, { loading: saving }] = useMutation(SaveBillingProfile, {
    refetchQueries,
  });

  const [updateSchedule, { loading: updatingSchedule }] = useMutation(UpdateBillingSchedule);

  const selectedSite = useMemo(() => {
    if (sitesData?.adminBillingProfileCoverageQuery) {
      return sitesData.adminBillingProfileCoverageQuery.find((site) => site._id === orgSiteId);
    }
    return null;
  }, [orgSiteId, sitesData]);

  const dbProfile = useMemo(() => {
    return billingProfileData?.adminBillingProfileQuery || null;
  }, [billingProfileData]);

  const { data: tabsContractData, loading: loadingContract } = useQuery(BillingContractsListQuery, {
    variables: {
      customerId: dbProfile?.tabsCustomerId || '',
    },
    skip: dbProfile === null,
  });

  const contractsList = useMemo(() => {
    return tabsContractData?.adminContractListQuery || [];
  }, [tabsContractData]);

  useEffect(() => {
    if (!loadingSites && dbProfile) {
      if (!selectedSite || !dbProfile.orgSiteIds.includes(selectedSite._id)) {
        setEditingProfile(undefined);
        return;
      }
      setEditingProfile({
        id: dbProfile.id,
        name: dbProfile.name,
        tabsCustomerId: dbProfile.tabsCustomerId,
        orgSiteIds: dbProfile.orgSiteIds,
        saasEventId: dbProfile.saasEventId,
        mmEventId: dbProfile.mmEventId,
      });
    } else {
      setEditingProfile(undefined);
    }
  }, [dbProfile, selectedSite, loadingSites]);

  const sitesList = useMemo(() => {
    return sitesData?.adminBillingProfileCoverageQuery || [];
  }, [sitesData]);

  const customerList = useMemo(() => {
    const usedCustomerIds = sitesList.reduce((ids: Set<string>, site: ProfileSite) => {
      if (site.billingProfileId) ids.add(site.billingProfileId);
      return ids;
    }, new Set<string>());
    if (dbProfile) usedCustomerIds.delete(dbProfile.tabsCustomerId);
    const list = tabsCustomerData?.adminBillingCustomerListQuery || [];
    return list.filter((cust) => !usedCustomerIds.has(cust.id));
  }, [tabsCustomerData, sitesList, dbProfile]);

  const eventTypesList = useMemo(() => {
    return tabsEventTypesData?.adminBillingEventTypeListQuery || [];
  }, [tabsEventTypesData]);

  const sitesMap = useMemo(() => {
    return sitesList.reduce((sMap: Map<string, string>, site: ProfileSite) => {
      sMap.set(site._id, site.siteName);
      return sMap;
    }, new Map<string, string>());
  }, [sitesList]);

  const orgSiteOptions = useMemo(() => {
    return sitesList.map((site: ProfileSite) => (
      <SelectOption key={site._id} value={site._id}>
        {site.siteName}
      </SelectOption>
    ));
  }, [sitesList]);

  const customerHubspotId = useMemo(() => {
    if (tabsCustomerData && editingProfile?.tabsCustomerId && editingProfile?.tabsCustomerId.length > 0) {
      const customer = tabsCustomerData.adminBillingCustomerListQuery.find(
        (tabsCustomer) => tabsCustomer.id === editingProfile.tabsCustomerId,
      );
      if (customer) return customer.hubspotId;
    }
    return null;
  }, [editingProfile, tabsCustomerData]);

  useEffect(() => {
    TitleService.setTitles('Sites Administration');
    Breadcrumbs.set([
      {
        url: routePaths.billingManager,
        title: 'Billing Manager',
      },
      {
        url: `${routePaths.billingManager}/org/${orgSiteId}`,
        title: selectedSite?.siteName || '',
      },
    ]);
  }, [orgSiteId, selectedSite]);

  const changeCustomerAssignment = (selection: SelectItem[]) => {
    const customerSelection = selection.length > 0 ? selection[0] : { text: '', value: '' };
    const customer = customerList.find((c: TabsCustomer) => c.id === customerSelection.value);
    let tabsCustomerId = '';
    let name = '';
    if (customer) {
      tabsCustomerId = customer.id;
      name = customer.customerName;
    }
    setEditingProfile((prev: BillingProfile | undefined) => {
      if (prev) return { ...prev, tabsCustomerId, name };
    });
    setTouched(true);
  };

  const changeOrgSiteConnections = (selection: SelectItem[]) => {
    setEditingProfile((prev: BillingProfile | undefined) => {
      if (prev) return { ...prev, orgSiteIds: selection.map((s) => s.value) };
    });
    setTouched(true);
  };

  const changeSaasEvent = (selection: SelectItem[]) => {
    const value = selection.length > 0 ? selection[0].value : '';
    setEditingProfile((prev: BillingProfile | undefined) => {
      if (prev) return { ...prev, saasEventId: value };
    });
    setTouched(true);
  };

  const changeMMEvent = (selection: SelectItem[]) => {
    const value = selection.length > 0 ? selection[0].value : '';
    setEditingProfile((prev: BillingProfile | undefined) => {
      if (prev) return { ...prev, mmEventId: value };
    });
    setTouched(true);
  };

  const { profileValid, errors } = useMemo(() => {
    let valid = true;
    const _errors: string[] = [];
    if (!editingProfile || !touched) return { profileValid: false, errors: [] };
    if (editingProfile.name === '') {
      valid = false;
    }
    if (editingProfile.orgSiteIds.length === 0) {
      valid = false;
      _errors.push('Please select at least one Seller Org');
    }
    if (editingProfile.tabsCustomerId === '') {
      valid = false;
      _errors.push('Please select a TABS Customer');
    }
    if (editingProfile.mmEventId === '' || editingProfile.saasEventId === '') {
      valid = false;
      _errors.push('Please select at least one event type');
    }
    return { profileValid: valid, errors: _errors };
  }, [editingProfile, touched]);

  const createNewBillingProfile = () => {
    const defaultSaaS = eventTypesList.find((ev: BillingEventType) => ev.name === 'SaaS');
    const defaultMM = eventTypesList.find((ev: BillingEventType) => ev.name === 'Marketplace');
    setEditingProfile({
      id: '',
      name: '',
      orgSiteIds: [orgSiteId],
      tabsCustomerId: '',
      mmEventId: defaultMM?.id || '',
      saasEventId: defaultSaaS?.id || '',
    });
  };

  const handleEditServiceStart = (e: React.MouseEvent<HTMLButtonElement>, contract: BillingContractType) => {
    e.preventDefault();
    setEditingContract({
      ...contract,
      touched: false,
      newServiceStartDate: moment(contract.serviceStartDate),
    });
  };

  const setContractStartDate = (newDate: moment.Moment) => {
    if (editingContract) {
      setEditingContract({
        ...editingContract,
        newServiceStartDate: newDate,
        touched: true,
      });
    }
  };

  const dateChangeDiff = useMemo(() => {
    if (!editingContract) return 0;
    return moment.duration(editingContract.newServiceStartDate.diff(editingContract.serviceStartDate)).as('days');
  }, [editingContract]);

  const saveBillingProfile = async () => {
    if (!editingProfile) return;
    try {
      const { id, name, orgSiteIds, tabsCustomerId, mmEventId, saasEventId } = editingProfile;
      const variables = {
        id,
        name,
        orgSiteIds,
        tabsCustomerId,
        mmEventId,
        saasEventId,
      };
      const res = await saveProfile({ variables });
      if (res.data && res.data.saveBillingProfile.errors.length > 0) {
        throw new Error(res.data.saveBillingProfile.errors[0]);
      }
      AlertService.alert({
        type: 'success',
        message: 'Billing Profile saved successfully!',
        autoDismiss: true,
        dismissDelay: 1000,
      });
    } catch (error) {
      AlertService.alert({
        type: 'warning',
        message: (error as Error).message,
        autoDismiss: false,
        dismissDelay: 1000,
      });
    }
  };

  const handleUpdateSchedule = async () => {
    if (!editingContract) return;
    try {
      const { id, commitmentId, scheduleId, obligationId, newServiceStartDate } = editingContract;
      const variables = {
        contractId: id,
        commitmentId,
        scheduleId,
        obligationId,
        serviceStartDate: newServiceStartDate.toISOString(),
      };
      const res = await updateSchedule({ variables });
      if (res.data && res.data.updateBillingSchedule.errors.length > 0) {
        throw new Error(res.data.updateBillingSchedule.errors[0]);
      }
      AlertService.alert({
        type: 'success',
        message: 'Contract saved successfully!',
        autoDismiss: true,
        dismissDelay: 1000,
      });
    } catch (error) {
      AlertService.alert({
        type: 'warning',
        message: (error as Error).message,
        autoDismiss: false,
        dismissDelay: 1000,
      });
    }
    setEditingContract(null);
  };

  return (
    <div className={classes.wrap}>
      <div className={classes.view}>
        {editingProfile && (
          <div className={classes.editBox}>
            <h2>Billing Customer Assignment</h2>
            {errors.length > 0 && (
              <div className={classes.errors}>
                {errors.map((err: string) => (
                  <div key={err} className={classes.error}>
                    {err}
                  </div>
                ))}
              </div>
            )}
            <BillingProfileView
              profile={editingProfile}
              sitesMap={sitesMap}
              orgSiteOptions={orgSiteOptions}
              customerList={customerList}
              eventTypesList={eventTypesList}
              changeCustomerAssignment={changeCustomerAssignment}
              changeOrgSiteConnections={changeOrgSiteConnections}
              changeSaasEvent={changeSaasEvent}
              changeMMEvent={changeMMEvent}
              customerHubspotId={customerHubspotId}
            />
            <Button
              onClick={saveBillingProfile}
              disabled={!touched || !profileValid}
              loading={saving}
              loadingText="Saving Profile..."
            >
              Save Billing Profile
            </Button>
          </div>
        )}
        {dbProfile && (
          <div className={classes.editBox}>
            <h2>Current Contract Details</h2>
            <Loading loading={loadingContract}>
              <div className={classes.contractBox}>
                {contractsList.map((contract: BillingContractType) => (
                  <div key={contract.id} className={classes.singleContract}>
                    <h4>{contract.contractDocName}</h4>

                    <div>
                      <strong>Status:</strong> {contract.status}
                    </div>
                    <div>
                      <h5>Service Term:</h5>
                      {contract.serviceStartDate !== null ? (
                        <div>
                          <span>
                            {moment(contract.serviceStartDate).format('MMM D, YYYY')} &rarr;{' '}
                            {moment(contract.serviceEndDate).format('MMM D, YYYY')}
                          </span>
                          <Button warning onClick={(e) => handleEditServiceStart(e, contract)}>
                            Edit Service Start
                          </Button>
                        </div>
                      ) : (
                        'Not Started'
                      )}
                      {contract.hasSentInvoices && <span>Warning, this customer has already been invoiced.</span>}
                    </div>
                    <div>
                      View Contract in{' '}
                      <a
                        href={`https://app.hubspot.com/file-preview/605444/file/${contract.hubspotId}/`}
                        target="_blank"
                        rel="noopener"
                      >
                        Hubspot
                      </a>{' '}
                      or{' '}
                      <a
                        href={`https://app.tabsplatform.com/merchant/contracts/${contract.id}`}
                        target="_blank"
                        rel="noopener"
                      >
                        TABS
                      </a>
                    </div>
                    {contract.revenue && contract.revenue.length > 0 && (
                      <div>
                        <h5>Revenue:</h5>
                        <table className={classes.revenueTable}>
                          <thead>
                            <tr>
                              <th>Month</th>
                              <th>Revenue</th>
                              <th>Books Open</th>
                            </tr>
                          </thead>
                          <tbody>
                            {contract.revenue?.map((month) => (
                              <tr key={month.timeframe}>
                                <td>{moment(`${month.timeframe}-01`).format('MMM YYYY')}</td>
                                <td>{accounting.formatMoney(month.total)}</td>
                                <td>{month.booksClosed ? 'No' : 'Yes'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <div>
                      View Usage Events:{' '}
                      <a
                        href={`https://app.tabsplatform.com/merchant/usage/events?sort=dateTime:desc&customerId=${editingProfile?.tabsCustomerId}&eventTypeId=${editingProfile?.saasEventId}`}
                        target="_blank"
                        rel="noopener"
                      >
                        SaaS
                      </a>
                      {' / '}
                      <a
                        href={`https://app.tabsplatform.com/merchant/usage/events?sort=dateTime:desc&customerId=${editingProfile?.tabsCustomerId}&eventTypeId=${editingProfile?.mmEventId}`}
                        target="_blank"
                        rel="noopener"
                      >
                        Managed Marketplace
                      </a>
                    </div>
                  </div>
                ))}
                {contractsList.length === 0 && <div>Could not find a contract commitment for this customer</div>}
              </div>
            </Loading>
          </div>
        )}
        {!editingProfile && selectedSite && !loadingProfile && (
          <div className={classes.noProfile}>
            <h3>{selectedSite.siteName} does not have a billing profile</h3>
            <Button onClick={createNewBillingProfile}>Create Billing Profile</Button>
          </div>
        )}
      </div>
      <Modal open={!!editingContract} closeOnEsc closeOnOutsideClick onHide={() => setEditingContract(null)}>
        <ModalContent>
          <h2>Edit Contract Start Date</h2>
          <p>Adjust the contract start date for {editingProfile?.name}.</p>
          <DatePicker
            label="New Contract Start Date"
            validRange={{ start: moment().subtract(1, 'year'), end: moment().add(1, 'year') }}
            onChange={setContractStartDate}
            day={editingContract?.newServiceStartDate}
            id={'date-picker'}
          />
          {editingContract?.touched && (
            <div>
              <span>Existing: {moment(editingContract.serviceStartDate).format(dateFormat)} &rarr;</span>{' '}
              <span className={dateChangeDiff > 0 ? classes.posShift : classes.negShift}>
                {dateChangeDiff > 0 ? '+' : '-'} {Math.round(Math.abs(dateChangeDiff))}{' '}
                {pluralize('day', dateChangeDiff)}
              </span>{' '}
              <span>&rarr; New: {editingContract.newServiceStartDate.format(dateFormat)}</span>
            </div>
          )}
        </ModalContent>
        <ModalFooter>
          <Button type="button" onClick={() => setEditingContract(null)} link>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!editingContract?.touched}
            loading={updatingSchedule}
            loadingText="Updating Contract..."
            onClick={handleUpdateSchedule}
          >
            Adjust Contract
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default BillingManagerEditor;
